import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { MdLocationOn, MdPhone, MdAccessTime } from 'react-icons/md'
import './Stores.css'

const stores = [
  { id: 1, name: 'BIGGUYS Electronic City', address: 'Electronic City, 2nd Cross Road, Phase 2', city: 'Bangalore', phone: '+91 9986180513', timing: '11 AM - 11 PM' },
  { id: 2, name: 'BIGGUYS Koramangala', address: '80 Feet Road, Koramangala 4th Block', city: 'Bangalore', phone: '+91 9986180514', timing: '11 AM - 11 PM' },
  { id: 3, name: 'BIGGUYS HSR Layout', address: '27th Main Road, HSR Sector 1', city: 'Bangalore', phone: '+91 9986180515', timing: '11 AM - 11 PM' },
  { id: 4, name: 'BIGGUYS Whitefield', address: 'ITPL Main Road, Whitefield', city: 'Bangalore', phone: '+91 9986180516', timing: '11 AM - 11 PM' },
  { id: 5, name: 'BIGGUYS Indiranagar', address: '100 Feet Road, CMH Road Junction', city: 'Bangalore', phone: '+91 9986180517', timing: '11 AM - 10 PM' },
  { id: 6, name: 'BIGGUYS Jayanagar', address: '4th Block, Jayanagar', city: 'Bangalore', phone: '+91 9986180518', timing: '11 AM - 10 PM' },
]

const cities = ['All', 'Bangalore', 'Hyderabad', 'Chennai']

export default function Stores() {
  const [activeCity, setActiveCity] = useState('All')
  const [searchQuery, setSearchQuery] = useState('')
  const pageRef = useRef(null)

  const filteredStores = stores.filter(store => {
    const matchesCity = activeCity === 'All' || store.city === activeCity
    const matchesSearch = store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         store.address.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCity && matchesSearch
  })

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero animation
      gsap.fromTo('.stores-hero-content > *',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', delay: 0.3 }
      )

      // Search section animation
      gsap.fromTo('.search-section',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.search-section',
            start: 'top 85%',
          }
        }
      )
    }, pageRef)

    return () => ctx.revert()
  }, [])

  useEffect(() => {
    // Animate store cards on filter change
    gsap.fromTo('.store-card',
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.4, stagger: 0.08, ease: 'power3.out' }
    )
  }, [activeCity, searchQuery])

  return (
    <div className="stores-page" ref={pageRef}>
      {/* Hero Section */}
      <section className="stores-hero">
        <div className="container">
          <div className="stores-hero-content">
            <span className="hero-label">Our Stores</span>
            <h1 className="hero-title">
              Find a BIGGUYS
              <span className="highlight"> Near You</span>
            </h1>
            <p className="hero-description">
              Locate the nearest BIGGUYS outlet and experience the crunchiest 
              fried chicken in town.
            </p>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="search-section">
        <div className="container">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by store name or location..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <svg className="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2"/>
              <path d="M13 13L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>

          <div className="city-filters">
            {cities.map((city) => (
              <button
                key={city}
                className={`filter-btn ${activeCity === city ? 'active' : ''}`}
                onClick={() => setActiveCity(city)}
              >
                {city}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stores Grid */}
      <section className="stores-grid-section">
        <div className="container">
          {filteredStores.length > 0 ? (
            <div className="stores-grid">
              {filteredStores.map((store) => (
                <div className="store-card" key={store.id}>
                  <div className="store-header">
                    <MdLocationOn size={22} color="#E4002B" className="store-icon" />
                    <span className="store-city">{store.city}</span>
                  </div>
                  <h3 className="store-name">{store.name}</h3>
                  <p className="store-address">{store.address}</p>
                  <div className="store-details">
                    <div className="detail-item">
                      <MdPhone size={18} color="#E4002B" className="detail-icon" />
                      <a href={`tel:${store.phone}`}>{store.phone}</a>
                    </div>
                    <div className="detail-item">
                      <MdAccessTime size={18} color="#E4002B" className="detail-icon" />
                      <span>{store.timing}</span>
                    </div>
                  </div>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(store.address + ', ' + store.city)}`} 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="btn btn-secondary btn-sm">
                    Get Directions
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <MdLocationOn size={50} color="#E4002B" className="no-results-icon" />
              <h3>No stores found</h3>
              <p>Try searching with a different keyword or city.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="stores-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Don't See a Store Near You?</h2>
            <p>We're expanding! Stay tuned for new locations or consider opening a franchise.</p>
            <a href="/franchise" className="btn btn-primary">Become a Franchisee</a>
          </div>
        </div>
      </section>
    </div>
  )
}
